import { useTheme } from "@/theme/ThemeProvider";
import { listRowContentA11y, listRowPressableA11y } from "@/ui/components/listRowA11y";
import { ENTITY_ICONS, entityColors, type EntityKind, type IconComponent } from "@/ui/entityVisuals";
import React from "react";
import { Pressable, Text, View, ViewStyle } from "react-native";

export type ListRowBadgeTone = "primary" | "neutral" | "warning" | "danger";

/**
 * Einheitliche, antippbare Listenzeile als Karte.
 * Deckt das wiederkehrende Muster ab: optionales Avatar-Icon, Titel + Untertitel,
 * optionaler Badge rechts und/oder ein eigener Trailing-Slot (z. B. Aktions-Icon).
 * Über `children` lassen sich zusätzliche Inhalte unter dem Untertitel einsetzen
 * (z. B. eine Reihe von Rollen-Chips).
 */
export function ListRow({
  title,
  subtitle,
  entity,
  leadingIcon: Leading,
  badge,
  badgeTone = "primary",
  trailing,
  onPress,
  accessibilityLabel,
  enforceHitTarget = false,
  children,
  titleNumberOfLines = 1,
  subtitleNumberOfLines = 1,
  style,
}: {
  title: string;
  subtitle?: string | null;
  /** Typ der Entität – setzt automatisch Avatar-Icon und -Farbe (einheitliche Bildsprache). */
  entity?: EntityKind;
  leadingIcon?: IconComponent;
  badge?: string | null;
  badgeTone?: ListRowBadgeTone;
  trailing?: React.ReactNode;
  onPress?: () => void;
  accessibilityLabel?: string;
  /** minHeight 44 und hitSlop nur für die Mitgliederseiten. */
  enforceHitTarget?: boolean;
  children?: React.ReactNode;
  titleNumberOfLines?: number;
  subtitleNumberOfLines?: number;
  style?: ViewStyle;
}) {
  const { theme } = useTheme();

  const badgeColors: Record<ListRowBadgeTone, { bg: string; text: string }> = {
    primary: { bg: theme.color.primary, text: theme.color.buttonText },
    neutral: { bg: theme.color.surfaceStrong, text: theme.color.muted },
    warning: { bg: theme.color.warningBg, text: theme.color.warning },
    danger: { bg: theme.color.dangerBg, text: theme.color.danger },
  };
  const bc = badgeColors[badgeTone];

  const mode = theme.mode === "dark" ? "dark" : "light";
  const LeadingIcon: IconComponent | undefined = entity ? ENTITY_ICONS[entity] : Leading;
  const ec = entity ? entityColors(entity, mode) : null;
  const avatarBg = ec ? ec.bg : theme.color.accentBg;
  const avatarFg = ec ? ec.fg : theme.color.accent;

  // Avatar-Größe an die Theme-Dichte koppeln (kompakter in „Klar“, größer in „Standard“).
  const avatarSize = theme.space.xxl + theme.space.md;
  const avatarIcon = Math.round(avatarSize * 0.55);

  const pressableA11y = listRowPressableA11y({ accessibilityLabel, enforceHitTarget });
  const contentA11y = listRowContentA11y({
    accessibilityLabel,
    pressable: Boolean(onPress),
  });
  const content = (
    <View
      {...contentA11y}
      style={[
        {
          flexDirection: "row",
          alignItems: "center",
          gap: theme.space.md,
          borderWidth: 1,
          borderColor: theme.color.border,
          backgroundColor: theme.color.surface,
          borderRadius: theme.radius.lg,
          padding: theme.space.cardPadding,
        },
        style,
      ]}
    >
      {LeadingIcon ? (
        <View
          style={{
            width: avatarSize,
            height: avatarSize,
            borderRadius: avatarSize / 2,
            backgroundColor: avatarBg,
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <LeadingIcon size={avatarIcon} color={avatarFg} weight="duotone" />
        </View>
      ) : null}

      <View style={{ flex: 1, minWidth: 0 }}>
        <Text
          style={{
            color: theme.color.text,
            fontWeight: "700",
            fontSize: theme.fontSize.body,
          }}
          numberOfLines={titleNumberOfLines}
        >
          {title}
        </Text>
        {subtitle ? (
          <Text
            style={{
              color: theme.color.muted,
              fontSize: theme.fontSize.small,
              marginTop: 2,
            }}
            numberOfLines={subtitleNumberOfLines}
          >
            {subtitle}
          </Text>
        ) : null}
        {children}
      </View>

      {badge ? (
        <View
          style={{
            paddingHorizontal: theme.space.sm,
            paddingVertical: theme.space.xs,
            borderRadius: theme.radius.sm,
            backgroundColor: bc.bg,
          }}
        >
          <Text style={{ fontSize: theme.fontSize.small, color: bc.text, fontWeight: "600" }}>
            {badge}
          </Text>
        </View>
      ) : null}

      {trailing}
    </View>
  );

  if (onPress) {
    return (
      <Pressable
        onPress={onPress}
        {...(pressableA11y.hitSlop != null ? { hitSlop: pressableA11y.hitSlop } : {})}
        {...(pressableA11y.accessibilityRole
          ? { accessibilityRole: pressableA11y.accessibilityRole }
          : {})}
        {...(pressableA11y.accessibilityLabel
          ? { accessibilityLabel: pressableA11y.accessibilityLabel }
          : {})}
        style={({ pressed }) => ({
          opacity: pressed ? 0.9 : 1,
          ...(pressableA11y.minHeight != null ? { minHeight: pressableA11y.minHeight } : {}),
        })}
      >
        {content}
      </Pressable>
    );
  }
  return content;
}
